(ns minorodata.components.transformations.header
  (:require ["date-fns" :refer [formatDistance parseISO]]
            [lucide-react :refer [CheckCircle2 ChevronDown Info PlusCircle
                                  Trash XCircle]]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.posthog-events :as posthog-events]
            [minorodata.app.utils :as app.utils]
            [minorodata.components.transformations.search :refer [search-button]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [ui.button :refer [button]]
            [ui.date-time :refer [date-tooltip]]
            [ui.dialog :refer [dialog-content dialog-root dialog-title]]
            [ui.dropdown-menu :refer [dropdown-menu-content dropdown-menu-item
                                      dropdown-menu-label dropdown-menu-root
                                      dropdown-menu-trigger]]
            [ui.page :refer [page-title]]
            [ui.typography :as typography]))

(defn select-environment [id]
  (dispatch [:environments.events/select-env id {:source "transforms"}]))

(defn show-create-environment-modal []
  (dispatch [:app.events/navigate-query {:create-environment true}]))

(defn show-delete-environment-modal []
  (dispatch [:app.events/navigate-query {:delete-environment true}]))

#_(defn show-restore-modal []
    (dispatch [:app.events/navigate-query {:restore true}]))

(defn show-diffs-modal [environment-id]
  (when environment-id
    (dispatch [:posthog/capture {:event (:transform-environment/compared posthog-events/events)
                                 :props {:environment-id environment-id
                                         :source         "transforms"}}]))
  (dispatch [:app.events/navigate-query {:compare true}]))

(defn show-merge-modal []
  (dispatch [:app.events/navigate-query {:merge true}]))

(defn environment-select [{:keys [environments active-environment]}]
  (let [prod-env?       @(re-posh/subscribe [:environments.subs/production-active?])
        active-env-name (cond
                          active-environment (:environment/name active-environment)
                          prod-env?          "Production"
                          :else              "Unknown environment")]
    [dropdown-menu-root
     [dropdown-menu-trigger {:asChild false}
      [button {:size       :icon-md
               :variant    :secondary
               :icon-right ChevronDown
               :component  :div
               :class      "w-[250px] truncate dark:text-white"
               :title      active-env-name
               :style      {:background-color (:environment/colour active-environment)}}
       active-env-name]]

     [dropdown-menu-content {:class "min-w-[250px]"}
      [:<>
       [dropdown-menu-item {:active   (not active-environment)
                            :on-click #(select-environment app.utils/production-env-id)}
        "Production"]
       [:hr {:class "my-0.5"}]
       [dropdown-menu-label {:class "font-medium"} "Dev environments"]
       (if (empty? environments)
         [dropdown-menu-label "No environments found"]
         (for [{:environment/keys [environment-id name colour]} environments]
           ^{:key environment-id}
           [dropdown-menu-item {:active   (= (:environment/environment-id active-environment) environment-id)
                                :on-click #(select-environment environment-id)
                                :title    name}
            [:div {:class "flex w-full justify-between items-center gap-2 overflow-hidden"}
             name
             [:div {:class "h-3 w-3 rounded-full"
                    :style {:background colour}}]]]))]]]))

(defn environment-actions [{:keys [environments]}]
  (let [prod? @(re-posh/subscribe [:environments.subs/production-active?])
        active-environment @(re-posh/subscribe [:environments.subs/active-environment])
        max-envs  (or (some-> @(re-posh/subscribe [:unleash/get-variant "fe.environments"])
                              (get :payload)
                              :value
                              (js/parseInt 10))
                      1)
        disabled-new-envs? (or (not max-envs)
                               (>= (count environments) max-envs))]
    [dropdown-menu-root
     ;; TODO: Remove disabled of tigger when possible to rollback environments
     [dropdown-menu-trigger {:asChild  false}
      [button {:size       :icon-md
               :variant    :secondary
               :icon-right ChevronDown
               :component  :div}
       "Environment actions"]]
     [dropdown-menu-content
      [dropdown-menu-item {:disabled prod?
                           :on-click show-delete-environment-modal}
       "Delete"]
      [:hr]
      [dropdown-menu-item {:disabled prod?
                           :on-click #(show-diffs-modal (:environment/environment-id active-environment))}
       "Compare to production"]
      [dropdown-menu-item {:disabled prod?
                           :on-click show-merge-modal}
       "Merge to production"]
      ;; Not used yet
      #_[:hr {:class "my-0.5"}]
      #_[dropdown-menu-item {:class    "flex gap-2 items-center justify-between"
                             :disabled (not prod?)
                             :on-click show-restore-modal}
         "Restore production" [:> AlertTriangle {:size 20}]]

      [dropdown-menu-item {:disabled prod?
                           :on-click (fn []
                                       (dispatch [:sql-console-modal.events/set-db-copy-env (:environment/environment-id active-environment)])
                                       (dispatch [:sql-console-modal.events/toggle-db-copy-modal true]))}
       "Copy data"]

      [:hr {:class "my-0.5"}]
      (when disabled-new-envs?
        [dropdown-menu-label {:class "text-xs font-medium opacity-50 max-w-[200px]"}
         (str "Maximum number of environments reached: " max-envs)])
      [dropdown-menu-item {:class    "flex items-center justify-between gap-2"
                           :disabled disabled-new-envs?
                           :on-click show-create-environment-modal}
       "Create environment"
       [:> PlusCircle {:size 20}]]]]))


(defn environment-overlay [{:keys [environment]}]
  [:div {:class "absolute inset-0 opacity-40 transition-colors"
         :style {:background (:environment/colour environment)}}])

(defn environment-controls [{:keys [active-environment]}]
  (let [panel @(re-posh/subscribe [:environments-panel/pull '[* {:environments-panel/environments [*]}]])
        {:environments-panel/keys [environments active-id]} panel]
    [:div {:class "flex gap-2 items-center"}
     [environment-select {:environments       environments
                          :active-environment active-environment}]
     [environment-actions {:active-id    active-id
                           :environments environments}]

     (when active-environment
       (let [created-at (parseISO (:environment/created-at active-environment))
             now       (js/Date.)]
         [:p "Created " (formatDistance now created-at) " ago"]))]))



(defn job-status-icon [{:keys [status]}]
  [:div
   (if (= status :failed)
     [:> XCircle {:class "text-kleene-text-error align-top"}]
     [:> CheckCircle2 {:class "text-kleene-text-success"}])])


(defn delete-job [job-id]
  (dispatch [:job/delete [:job/id job-id]]))

(defn jobs-status-items [{:keys [jobs]}]
  (if (empty? jobs)
    [typography/h3 {:color   "textSecondary"
                    :align   "center"
                    :variant "subtitle1"}
     "No finished transforms so far."]

    (into
     [:div {:class "divide-y"}]
     (for [{:keys [id name started finished status error]} jobs]
       ^{:key id}
       [:div {:class "flex gap-4 items-center py-4 px-2"}
        [job-status-icon
         {:status status}]

        [:div {:class "flex-1 overflow-hidden"}
         [typography/h4 {:class "truncate" :title name} name]
         [:div
          [:<>
           [typography/p
            [:span {:class "text-kleene-text-secondary"} "Started - "]
            [date-tooltip {:date started}]]

           [typography/p
            [:span {:class "text-kleene-text-secondary"} "Finished - "]
            [date-tooltip {:date finished}]]

           (when (= status :failed)
             [typography/p {:class "overflow-x-auto"}
              (if (some? error)
                error
                "An error occurred while launching this job. Please check the logs page to see the details.")])]]]

        [:div {}
         [button {:icon     Trash
                  :size     :icon-md
                  :variant  :secondary
                  :on-click #(delete-job id)}]]]))))

(defn launch-status-button []
  (let [open?               (reagent/atom false)
        open-launch-dialog  #(reset! open? true)
        close-launch-dialog #(reset! open? false)
        finished-jobs       (re-posh/subscribe [:transformations.subs/finished-jobs])]
    (fn []
      [:<>
       (when-not (empty? @finished-jobs)
         [:div
          [button {:variant   :secondary
                   :size      "small"
                   :icon      Info
                   :on-click  open-launch-dialog}
           "Launch status"]])

       [dialog-root {:open           @open?
                     :on-open-change close-launch-dialog}
        [dialog-content {:size :medium}
         [dialog-title "Finished transform runs"]

         [jobs-status-items
          {:jobs @finished-jobs}]]]])))

(defn header []
  (let [active-environment @(re-posh/subscribe [:environments.subs/active-environment])]

    [:<>
     [environment-overlay {:environment active-environment}]

     [:div {:class "relative flex justify-between items-end gap-4"
            :style {:z-index 1}}
      [:div {:class "flex flex-col gap-2"}
       [page-title "Transforms"]
       (when @(re-posh/subscribe [:environments.subs/enabled?])
         [environment-controls {:active-environment active-environment}])]

      [:div {:class "flex gap-2"}
       [launch-status-button]
       [search-button]]]]))
